/**
 * AltbestandTaskSection.tsx - the task «Altbestand prüfen» of one mandate in the Aufgaben peek: the rows
 * whose deadline (or planning) lies before the reminder loop's cutoff, each with its insurer, status and
 * Frist, and the insurers of a Brokermandat still without a Meldung. The loop never mails them by itself;
 * a broker acts here with the usual actions, each shown only to a caller who holds every key its route
 * enforces: «Jetzt erinnern» and «Freigeben» (communication.send + mandate.eingangskontrolle),
 * «Schliessen» with a Notiz (crm.write; the Brokermandat's or the analysis notification's route) and
 * «Meldung auslösen» for the unreported insurers (communication.send + mandate.eingangskontrolle).
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatZurichDateDe } from '@brokly/shared';
import type { RegistrationDto } from '@brokly/shared';
import { Button } from '../../components/ui/Button';
import { apiClient } from '../../lib/api-client';
import { qk } from '../../lib/query-keys';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';
import { isNotFound, restartRegistrationFollow, startRegistrationWatch, useFollowRegistrationJob, useRegistrationAltbestand } from './mandate-registration-queries';
import { REGISTRATION_CLOSE_KEY, REGISTRATION_SEND_KEYS, RegistrationSendingLine } from './MandateRegistrationPanel';
import { registrationErrorText } from './registration-error-text';

type Action =
  | { action: 'erinnern' | 'freigeben'; row: RegistrationDto }
  | { action: 'schliessen'; row: RegistrationDto; notiz: string }
  | { action: 'run' };

export function AltbestandTaskSection({ taskId, subjectType, subjectId }: { taskId: string; subjectType: 'person' | 'firma'; subjectId: string }) {
  const view = useRegistrationAltbestand(taskId);
  const { can } = useEffectivePermissions();
  const canSend = REGISTRATION_SEND_KEYS.every((k) => can(k));
  const canClose = can(REGISTRATION_CLOSE_KEY);
  const [closing, setClosing] = useState<string | null>(null);
  const [notiz, setNotiz] = useState('');
  const qc = useQueryClient();
  const mandateId = view.data?.mandate_id ?? '';
  // «Meldung auslösen» here is followed like the panel's: the section refreshes once the job lands.
  const sending = useFollowRegistrationJob(mandateId, { subjectType, subjectId }, { enabled: view.data?.mandate_kind === 'broker' });
  const act = useMutation<unknown, Error, Action>({
    mutationFn: (a) => {
      switch (a.action) {
        case 'erinnern':
          return apiClient.registrations.erinnern(mandateId, a.row.id);
        case 'freigeben':
          return apiClient.registrations.freigeben(mandateId, a.row.id);
        case 'schliessen':
          return a.row.prozess === 'p5_broker'
            ? apiClient.registrations.schliessen(mandateId, a.row.id, { notiz: a.notiz })
            : apiClient.versichererBenachrichtigung.schliessen(mandateId, a.row.id, { notiz: a.notiz });
        case 'run':
          return apiClient.registrations.run(mandateId);
        default: {
          const unhandled: never = a;
          throw new Error(`unhandled action ${String(unhandled)}`);
        }
      }
    },
    onSuccess: (_res, a) => {
      if (a.action === 'run') startRegistrationWatch(qc, mandateId);
      else if (a.action === 'freigeben') restartRegistrationFollow(qc, mandateId);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['registrations'] });
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      void qc.invalidateQueries({ queryKey: qk.versichererBenachrichtigung() });
      void qc.invalidateQueries({ queryKey: qk.registrationTile(subjectType, subjectId) });
    },
  });

  if (view.isError) {
    if (isNotFound(view.error)) return null;
    return (
      <p className="text-sm text-destructive" role="alert">
        {registrationErrorText(view.error)}
      </p>
    );
  }
  const data = view.data;
  if (!data) return null;

  return (
    <div className="flex flex-col gap-2 text-sm" data-testid="alt-section">
      <p className="text-xs text-muted-foreground">
        Diese Meldungen liegen vor dem Start der automatischen Erinnerungen. Brokly mailt die Versicherer hier nicht selbst: bitte jede prüfen.
      </p>
      {act.isError && (
        <p className="text-sm text-destructive" role="alert">
          {registrationErrorText(act.error)}
        </p>
      )}
      <ul className="flex flex-col gap-2">
        {data.registrations.map((r) => (
          <li key={r.id} className="flex flex-col gap-1 rounded-md border px-3 py-2" data-testid={`alt-row-${r.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{r.carrier_name ?? r.carrier_uid}</span>
              <span className="rounded bg-muted px-1.5 py-0.5 text-xs">{r.status_label}</span>
              {r.response_due_at && <span className="text-xs text-muted-foreground">Frist bis: {formatZurichDateDe(r.response_due_at)}</span>}
            </div>
            <div className="flex flex-wrap gap-2">
              {canSend && r.actions.erinnern && (
                <Button size="sm" variant="outline" disabled={act.isPending} onClick={() => act.mutate({ action: 'erinnern', row: r })} data-testid="alt-erinnern">
                  Jetzt erinnern
                </Button>
              )}
              {canSend && r.actions.freigeben && (
                <Button size="sm" variant="outline" disabled={act.isPending} onClick={() => act.mutate({ action: 'freigeben', row: r })} data-testid="alt-freigeben">
                  Freigeben
                </Button>
              )}
              {canClose && r.actions.schliessen && (
                <Button size="sm" variant="outline" onClick={() => setClosing(r.id)} data-testid="alt-schliessen">
                  Schliessen
                </Button>
              )}
            </div>
            {closing === r.id && (
              <div className="flex flex-wrap items-center gap-2">
                <input className="min-w-48 flex-1 rounded border px-2 py-1 text-sm" placeholder="Notiz" aria-label="Notiz" value={notiz} onChange={(e) => setNotiz(e.target.value)} />
                <Button
                  size="sm"
                  disabled={act.isPending || notiz.trim() === ''}
                  onClick={() =>
                    act.mutate(
                      { action: 'schliessen', row: r, notiz },
                      {
                        onSuccess: () => {
                          setClosing(null);
                          setNotiz('');
                        },
                      },
                    )
                  }
                  data-testid="alt-schliessen-confirm"
                >
                  Schliessen
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setClosing(null)}>
                  Abbrechen
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
      {data.unreported_carriers.length > 0 && (
        <div className="flex flex-col gap-1 rounded-md border px-3 py-2" data-testid="alt-unreported">
          <span className="text-xs font-medium">Noch nicht gemeldet</span>
          <span>{data.unreported_carriers.map((c) => c.carrier_name ?? c.carrier_uid ?? 'Versicherer ohne UID').join(', ')}</span>
          {canSend && data.mandate_kind === 'broker' && (
            <div>
              <Button size="sm" variant="outline" disabled={act.isPending} onClick={() => act.mutate({ action: 'run' })} data-testid="alt-run">
                Meldung auslösen
              </Button>
            </div>
          )}
        </div>
      )}
      {sending && <RegistrationSendingLine follow={sending} testId="alt-sending" />}
    </div>
  );
}
